/**
 * Where to send a user after they sign in.
 *
 * A pure module with tests because it is a security decision, not a convenience: the value it
 * returns is fed to the router, and a path taken from a URL parameter is an open-redirect surface.
 * Written this way from the start rather than validated after the fact, since the naive version
 * (trust the query parameter, redirect straight to it) is the one that gets shipped first.
 *
 * The rules, each with the case that motivates it:
 *
 *   - **Router state beats a query parameter.** `location.state.from` can only have been set by
 *     this application; `?next=` can be set by anyone who can get a user to click a link.
 *   - **Only same-origin absolute paths.** A candidate must start with exactly one `/`. `//evil.example`
 *     is a protocol-relative URL that browsers treat as another origin, and `/\evil.example` is the
 *     same attack through a character some parsers normalise to `/`.
 *   - **No scheme, no control characters, no whitespace.** `javascript:`, `data:`, and a `\n`
 *     smuggled through an encoder are all ways to make a string stop being a path.
 *   - **Never back to an auth page.** Redirecting to `/login` after logging in is an infinite
 *     bounce that reads to the user as the password having been wrong.
 *
 * Anything that fails a rule falls back to the home route rather than erroring. A refused redirect
 * is not worth an error screen; it is worth not following.
 */

/** Where a user lands when there is no valid stored destination. */
export const DEFAULT_REDIRECT = '/';

/** Paths a signed-in user must never be sent back to. */
const AUTH_PATHS: readonly string[] = ['/login', '/logout'];

/**
 * Whitespace and C0/C1 control characters. Written as a code-point scan rather than a regex
 * because `no-control-regex` (correctly) objects to control characters written into a pattern, and
 * the escape needed to satisfy it is less readable than this.
 */
function hasUnsafeCharacter(value: string): boolean {
  for (const character of value) {
    const code = character.codePointAt(0) ?? 0;
    if (code <= 0x20 || (code >= 0x7f && code <= 0x9f)) return true;
  }
  return false;
}

function isSafePath(candidate: string): boolean {
  if (!candidate.startsWith('/')) return false;
  // Protocol-relative (`//host`) and the backslash variant browsers normalise to it.
  if (candidate.startsWith('//') || candidate.startsWith('/\\')) return false;
  if (hasUnsafeCharacter(candidate)) return false;
  // A scheme cannot appear in a path that starts with `/`, but an encoder or a router that strips
  // the leading slash can make one appear later. Refusing outright is cheaper than reasoning about
  // which normalisation ran.
  if (candidate.includes(':')) return false;

  const [pathname] = candidate.split('?');
  if (pathname === undefined) return false;
  return !AUTH_PATHS.includes(pathname);
}

export interface RedirectCandidates {
  /** `location.state.from`, set by this app's own route guard. Trusted first. */
  readonly fromState?: unknown;
  /** The raw `?next=` value, if any. Untrusted. */
  readonly fromQuery?: string | null;
}

/** The first candidate that passes every rule, or `DEFAULT_REDIRECT`. */
export function resolveRedirectTarget(candidates: RedirectCandidates): string {
  const { fromState, fromQuery } = candidates;

  if (typeof fromState === 'string' && isSafePath(fromState)) {
    return fromState;
  }
  if (typeof fromQuery === 'string' && isSafePath(fromQuery)) {
    return fromQuery;
  }
  return DEFAULT_REDIRECT;
}
